import React, { useCallback, useState } from 'react';

import { closestCenter, DndContext, DragEndEvent, DragOverlay } from '@dnd-kit/core';
import { arrayMove, SortableContext, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { always, isEmpty } from 'ramda';
import { createPortal } from 'react-dom';

import { IGuestsProps } from 'components/shared/ArrayFields/types';
import { SortableWrapper, SortableWrapperLayout } from 'components/shared/SortableWrapper';
import { getGuestNameInfo } from 'utils/itineraries/functions';

import { AddNewGuest } from '../AddNewGuest';
import GuestCard from '../GuestCard';

type Props = {
  value: IGuestsProps[];
  onChange: (value: IGuestsProps[]) => void;
  handleDeleteGuest: (guest: IGuestsProps) => Promise<void>;
  handleClickEditGuestButton: (guest: IGuestsProps) => void;
  handleClickAddNewGuestButton: () => void;
  handleClickGuestSearch: () => void;
};

function GuestsAdapter({
  value,
  onChange,
  handleDeleteGuest,
  handleClickEditGuestButton,
  handleClickAddNewGuestButton,
  handleClickGuestSearch,
}: Props) {
  const [draggingItemId, setDraggingItemId] = useState<number | null>(null);
  const isNoGuests = isEmpty(value);
  const isDndActive = value?.length > 1;

  const renderDruggingOverlay = useCallback(() => {
    if (!draggingItemId) return null;

    const druggingItem = value.find((guest) => guest.id === draggingItemId);
    if (!druggingItem) return null;

    const { name, isEmpty } = getGuestNameInfo(druggingItem);

    return createPortal(
      <DragOverlay adjustScale={false}>
        <SortableWrapperLayout withDivider withDnd={isDndActive}>
          <GuestCard isHasName={!isEmpty} onClickDelete={always(null)} onClickEdit={always(null)}>
            {name}
          </GuestCard>
        </SortableWrapperLayout>
      </DragOverlay>,
      document.body,
    );
  }, [draggingItemId, value]);

  const handleDragEnd = (event: DragEndEvent) => {
    document.body.classList.remove('dragging');
    const { active, over } = event;
    if (!(active && over)) {
      return;
    }

    if (active.id !== over.id) {
      const oldIndex = value.map((e) => e.id).indexOf(Number(active.id));
      const newIndex = value.map((e) => e.id).indexOf(Number(over?.id));

      const movedGuests = arrayMove(value, oldIndex, newIndex);
      const guestsWithPositions = movedGuests.map((guest, index) => ({ ...guest, position: index }));

      onChange(guestsWithPositions);
    }
  };

  return (
    <React.Fragment>
      {!isNoGuests && (
        <DndContext
          collisionDetection={closestCenter}
          onDragStart={({ active }) => {
            if (!active) {
              return;
            }
            document.body.classList.add('dragging');
            setDraggingItemId(Number(active.id));
          }}
          onDragEnd={handleDragEnd}
          onDragCancel={() => {
            document.body.classList.remove('dragging');
            setDraggingItemId(null);
          }}
        >
          <SortableContext items={value} strategy={verticalListSortingStrategy}>
            {value.map((guest) => {
              const { name, isEmpty } = getGuestNameInfo(guest);

              return (
                <SortableWrapper key={guest.id} id={guest.id} withDivider withDnd={isDndActive}>
                  <GuestCard
                    isHasName={!isEmpty}
                    onClickDelete={() => handleDeleteGuest(guest)}
                    onClickEdit={() => handleClickEditGuestButton(guest)}
                  >
                    {name}
                  </GuestCard>
                </SortableWrapper>
              );
            })}

            {renderDruggingOverlay()}
          </SortableContext>
        </DndContext>
      )}

      <AddNewGuest
        btnText={isNoGuests ? 'Add Guest' : 'Add New Guest'}
        onShowGuestForm={handleClickAddNewGuestButton}
        onShowSearchForm={handleClickGuestSearch}
      />
    </React.Fragment>
  );
}

export default GuestsAdapter;
